import React from 'react';
import PropTypes from 'prop-types';
import FontIcon from 'components/FontIcon';
import Link from 'components/Link';
import classNames from 'classnames';
import ButtonAClasses from './ButtonA.module.scss';
import ButtonBClasses from './ButtonB.module.scss';
import ButtonCClasses from './ButtonC.module.scss';
import ButtonDClasses from './ButtonD.module.scss';
import ButtonEClasses from './ButtonE.module.scss';

const Button = (props) => {
  const {
    actionType,
    subType,
    title,
    href,
    target,
    leftIcon,
    rightIcon,
    onClick,
    className,
    disabled,
    tabIndex,
  } = props;

  const buttonClasses = {
    ButtonA: ButtonAClasses,
    ButtonB: ButtonBClasses,
    ButtonC: ButtonCClasses,
    ButtonD: ButtonDClasses,
    ButtonE: ButtonEClasses,
  };

  const classes = buttonClasses[subType];

  const Component = href ? Link : 'button';
  const conditionalProps = href ? { href, target } : {};

  return (
    <Component
      {...conditionalProps}
      type={actionType}
      className={classNames(classes.container, className)}
      onClick={onClick}
      disabled={disabled}
      aria-label={title}
      tabIndex={tabIndex}
    >
      {leftIcon && <FontIcon className={classes.leftIcon} icon={leftIcon} />}
      {title && <span
        className={classes.title}
        dangerouslySetInnerHTML={{ __html: title }}
      />}
      {rightIcon && <FontIcon className={classes.rightIcon} icon={rightIcon} />}
    </Component>
  );
};

Button.propTypes = {
  /**
   * Type of button<br>
   * Options: `button` `reset` `submit`
  */
  actionType: PropTypes.string,
  /**
   * Button Template<br>
   * Options: `buttonA` `buttonB` `buttonC` `buttonD` `buttonE`
  */
  subType: PropTypes.string.isRequired,
  /**
   * Text displays in the center of the component
  */
  title: PropTypes.string,
  /**
   * Button link that will be navigated to when clicked
  */
  href: PropTypes.string,
  /**
   * Where to display the response after click<br>
   * Options: `_blank` `_self` `_parent` `_top`
  */
  target: PropTypes.string,
  /**
   * Button icon on the left side of the title<br>
   * Example: `far fa-home`<br>
   * https://fontawesome.com/icons?d=gallery to search available icons
  */
  leftIcon: PropTypes.string,
  /**
   * Button icon on the right side of the title<br>
   * Example: `far fa-home`<br>
   * https://fontawesome.com/icons?d=gallery to search available icons
  */
  rightIcon: PropTypes.string,
  /**
   * Event that happens on the button click
  */
  onClick: PropTypes.func,
  /**
   * Custom css class to be applied
  */
  className: PropTypes.string,
  /**
   * Show button in disabled or enabled state<br>
   * Options: `true` `false`
  */
  disabled: PropTypes.bool,
  /**
   * tab-index property
  */
  tabIndex: PropTypes.number,
};

export default Button;
